<template>
  <nav class="flex-1 px-2 py-4 space-y-1">
    <!-- Main menu items -->
    <template v-for="item in navigationItems" :key="item.name">
      <!-- Regular menu item (no children) -->
      <RouterLink
        v-if="!item.children && item.path"
        :to="item.path"
        :class="[
          'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
          isActive(item.path)
            ? 'bg-blue-100 text-blue-900'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        ]"
        @click="$emit('navigate')"
      >
      <!-- Home Icon -->
      <svg
        v-if="item.icon === 'home'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
      
      <!-- Truck Icon -->
      <svg
        v-else-if="item.icon === 'truck'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      
      <!-- User Icon -->
      <svg
        v-else-if="item.icon === 'user'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
      
      <!-- Calendar Icon -->
      <svg
        v-else-if="item.icon === 'calendar'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      
      <!-- Chart Icon -->
      <svg
        v-else-if="item.icon === 'chart'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      
      <!-- Cog Icon -->
      <svg
        v-else-if="item.icon === 'cog'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      
      <!-- Bell Icon -->
      <svg
        v-else-if="item.icon === 'bell'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      
      <!-- Flask Icon -->
      <svg
        v-else-if="item.icon === 'flask'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
      
      <!-- Clock Icon -->
      <svg
        v-else-if="item.icon === 'clock'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>

      <!-- Users Icon -->
      <svg
        v-else-if="item.icon === 'users'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>

      <!-- Shield Icon -->
      <svg
        v-else-if="item.icon === 'shield'"
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>

      <!-- Default Icon -->
      <svg
        v-else
        :class="[
          'mr-3 h-5 w-5 flex-shrink-0',
          isActive(item.path || '')
            ? 'text-blue-500'
            : 'text-gray-400 group-hover:text-gray-500'
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
      {{ item.name }}
    </RouterLink>

    <!-- Menu item with children (expandable) -->
    <div v-else>
      <button
        @click="toggleSubmenu(item.name)"
        :class="[
          'group w-full flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-colors',
          isSubmenuActive(item)
            ? 'bg-blue-100 text-blue-900'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        ]"
      >
        <!-- Cog Icon for Settings -->
        <svg
          v-if="item.icon === 'cog'"
          :class="[
            'mr-3 h-5 w-5 flex-shrink-0',
            isSubmenuActive(item)
              ? 'text-blue-500'
              : 'text-gray-400 group-hover:text-gray-500'
          ]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
         <span class="whitespace-nowrap">
    {{ item.name }}
  </span>
        <!-- Chevron for expand/collapse -->
        <svg
          :class="[
      'ml-auto h-4 w-4 transition-transform duration-200 flex-shrink-0',
      expandedSubmenus.includes(item.name) ? 'rotate-90' : ''
    ]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        
       
      </button>
      
      <!-- Submenu items -->
      <div
        v-show="expandedSubmenus.includes(item.name)"
        class="mt-1 space-y-1"
      >
        <RouterLink
          v-for="child in item.children"
          :key="child.name"
          :to="child.path || ''"
          :class="[
            'group flex items-center pl-8 pr-2 py-2 text-sm font-medium rounded-md transition-colors',
            isActive(child.path || '')
              ? 'bg-blue-100 text-blue-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          ]"
          @click="$emit('navigate')"
        >
          <!-- Bell Icon for Notifications -->
          <svg
            v-if="child.icon === 'bell'"
            :class="[
              'mr-3 h-4 w-4 flex-shrink-0',
              isActive(child.path || '')
                ? 'text-blue-500'
                : 'text-gray-400 group-hover:text-gray-500'
            ]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          
          <!-- Flask Icon for LINE Test -->
          <svg
            v-else-if="child.icon === 'flask'"
            :class="[
              'mr-3 h-4 w-4 flex-shrink-0',
              isActive(child.path || '')
                ? 'text-blue-500'
                : 'text-gray-400 group-hover:text-gray-500'
            ]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          
          <!-- Clock Icon for Thai Date Test -->
          <svg
            v-else-if="child.icon === 'clock'"
            :class="[
              'mr-3 h-4 w-4 flex-shrink-0',
              isActive(child.path || '')
                ? 'text-blue-500'
                : 'text-gray-400 group-hover:text-gray-500'
            ]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          

          
          {{ child.name }}
        </RouterLink>
      </div>
    </div>
  </template>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { useAuthStore } from '../stores/authStore'

// Define type for navigation items
interface NavigationItem {
  name: string
  path?: string
  icon: string
  children?: NavigationItem[]
  requiresAdmin?: boolean
}

// State for expanded submenus
const expandedSubmenus = ref<string[]>([])

const authStore = useAuthStore()
const route = useRoute()

// Menu structure
const allMenuItems: NavigationItem[] = [
  { name: 'แดชบอร์ด', path: '/', icon: 'home' },
  { name: 'ตารางงาน', path: '/schedules', icon: 'calendar' },
  { name: 'ยานพาหนะ', path: '/vehicles', icon: 'truck' },
  { name: 'คนขับ', path: '/drivers', icon: 'user' },
  { name: 'ชั่วโมงขับรถ', path: '/driving-hours', icon: 'clock' },
  { name: 'รายงาน', path: '/reports', icon: 'chart' },
  { name: 'จัดการสมาชิก', path: '/users', icon: 'users', requiresAdmin: true },
  {
    name: 'การตั้งค่า',
    icon: 'cog',
    requiresAdmin: true, // เฉพาะ admin เท่านั้น
    children: [
      { name: 'การแจ้งเตือน', path: '/notifications', icon: 'bell' },
      { name: 'ทดสอบ LINE', path: '/line-notification-test', icon: 'flask' },
      { name: 'ทดสอบ Notiflix', path: '/notiflix-demo', icon: 'bell' },
      { name: 'ทดสอบวันที่ไทย', path: '/thai-datetime-demo', icon: 'clock' }
    ]
  }
]

// Emit events
defineEmits<{
  navigate: []
}>()

// Check if current route is active
const isActive = (path: string) => {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path.startsWith(path)
}

// Toggle submenu expansion
const toggleSubmenu = (itemName: string) => {
  const index = expandedSubmenus.value.indexOf(itemName)
  if (index > -1) {
    expandedSubmenus.value.splice(index, 1)
  } else {
    expandedSubmenus.value.push(itemName)
  }
}

// Check if submenu is active (any child is active)
const isSubmenuActive = (item: NavigationItem) => {
  if (!item.children) return false
  return item.children.some(child => child.path && isActive(child.path))
}

// Check if user has permission to access a menu item
const hasMenuPermission = (item: NavigationItem): boolean => {
  // Admin-only items
  if (item.requiresAdmin) {
    return authStore.isAdmin
  }

  // Everyone can access non-admin items
  return true
}

// Filter menu items based on user role
const navigationItems = computed<NavigationItem[]>(() => {
  return allMenuItems.filter(item => {
    // Check if user has permission for this item
    if (!hasMenuPermission(item)) {
      return false
    }

    // If item has children, filter them too
    if (item.children) {
      const filteredChildren = item.children.filter(child => hasMenuPermission(child))
      // Only show parent if it has visible children
      if (filteredChildren.length === 0) {
        return false
      }
      // Update children with filtered list
      item.children = filteredChildren
    }

    return true
  })
})

// Auto-expand settings menu if any child route is active
const initializeExpandedSubmenus = () => {
  const settingsItem = navigationItems.value.find((item: NavigationItem) => item.name === 'การตั้งค่า')
  if (settingsItem && settingsItem.children) {
    const hasActiveChild = settingsItem.children.some((child: NavigationItem) =>
      child.path && isActive(child.path)
    )
    if (hasActiveChild && !expandedSubmenus.value.includes('การตั้งค่า')) {
      expandedSubmenus.value.push('การตั้งค่า')
    }
  }
}

// Initialize on component mount
initializeExpandedSubmenus()
</script>